import React, { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import WebViewer from "@pdftron/webviewer";

// Material-UI Core
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Fade from "@material-ui/core/Fade";

// Material-UI Lab
import ToggleButton from "@material-ui/lab/ToggleButton";

// Material-UI Icon
import MenuOpenIcon from "@material-ui/icons/MenuOpen";

// Custom Component
import useLoading from "../Loading/useLoading";
import LockScreenLoading from "../LockScreenLoading/LockScreenLoading";
import FileManagerNav from "./FileManagerNav";
import FileManagerExplorer from "./FileManagerExplorer";
import EnhancedTable from "../Table/EnhancedTable";
import { Alert } from "../Alert/Alert";
import ConfirmDialog from "../Dialog/ConfirmDialog";
import NormalDialog from "../Dialog/NormalDialog";
import FullScreenDialog from "../Dialog/FullScreenDialog";
import CreateFolderForm from "./CreateFolderForm";
import PropertyDetailForm from "./PropertyDetailForm";
import AdvancedSearchForm from "./AdvancedSearchForm";

// Custom Style
import efilingStyle from "../../styles/jss/components/fileManager/fileManagerStyle";

// Fetch Data
import {
  getFolderList,
  getFileList,
  createFolder,
  removeFolder,
  removeFile,
  moveProperty,
  updatePropertyDetail,
  propertyDownload,
  advancedSearch,
} from "../../dataSource/fileManager";
import { saveAnnotation } from "../../dataSource/webViewer";

const useStyles = makeStyles((theme) => efilingStyle(theme));

const FileManager = (props) => {
  const store = require("store");
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { fileManagerType } = props;
  const screenSize = useMediaQuery(theme.breakpoints.up("md"))
    ? `${
        window.outerHeight - (window.outerHeight - window.innerHeight + 147)
      }px`
    : "auto";

  // Redux Data
  const folderList = useSelector((state) => state.folderList);
  const folderNavResult = useSelector((state) => state.folderNav);
  const folderSelectedResult = useSelector(
    (state) => state.folderSelectedRedux
  );
  const propertyDetailResult = useSelector((state) => state.propertyDetail);
  const propertySeqRedux = useSelector((state) => state.propertySeqRedux);
  const advancedSearchRedux = useSelector((state) => state.advancedSearchRedux);
  const userModulesRedux = useSelector((state) => state.userModulesRedux);
  const companyListByFolderRedux = useSelector(
    (state) => state.companyListByFolderRedux
  );

  // Loading
  const [
    fetchFolderListLoading,
    fetchFolderListShowLoading,
    fetchFolderListHideLoading,
  ] = useLoading();
  const [
    fetchFileListLoading,
    fetchFileListShowLoading,
    fetchFileListHideLoading,
  ] = useLoading();

  const [explorerOpen, setExplorerOpen] = useState(true);
  const [expanded, setExpanded] = useState([]);
  const [selected, setSelected] = useState([]);
  const [propertySelected, setPropertySelected] = useState([]);
  const [explorerSwitchSelected, setExplorerSwitchSelected] = useState(false);
  const [fileTableMdWidth, setFileTableMdWidth] = useState(8);
  const [fileTableLgWidth, setFileTableLgWidth] = useState(9);
  const [fileList, setFileList] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [nextPage, setNextPage] = useState(1);
  const [textFolderName, setTextFolderName] = useState("");
  const [addFolderDialogOpen, setAddFolderDialogOpen] = useState(false);
  const [propertyDetailDialogOpen, setPropertyDetailDialogOpen] =
    useState(false);
  const [fileSearchDialogOpen, setFileSearchDialogOpen] = useState(false);
  const [folderCreated, setFolderCreated] = useState("");
  const [folderRemoved, setFolderRemoved] = useState("");
  const [fileRemoved, setFileRemoved] = useState("");
  const [fileUploaded, setFileUploaded] = useState(null);
  const [explorerRefresh, setExplorerRefresh] = useState("");
  const [fileRefresh, setFileRefresh] = useState("");

  const [resetFileSelect, setResetFileSelect] = useState(false);
  const [resetMovePropertyControl, setResetMovePropertyControl] =
    useState(false);
  const [quickSearchKeyWord, setQuickSearchKeyWord] = useState("");
  const [propertyFileSeq, setPropertyFileSeq] = useState(null);
  const [primaryFilename, setPrimaryFilename] = useState(null);
  const [createFolderValidationErrors, setCreateFolderValidationErrors] =
    useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [lockScreenLoadingOpen, setLockScreenLoadingOpen] = useState(false);
  const [progressTitle, setProgressTitle] = useState("");
  const [folderRight, setFolderRight] = useState(null);
  const [webViewerProps, setWebViewerProps] = useState(null);

  // const [filePropertyDetailDialogBtn, setFilePropertyDetailDialogBtn] = useState([])

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmDialogData, setConfirmDialogData] = useState({
    dialogTitle: "",
    dialogContent: "",
    confirmAction: "",
    cancelAction: "",
  });

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertData, setAlertData] = useState({
    type: "",
    title: "",
    content: "",
  });

  const handleExplorerSwitch = () => {
    setExplorerOpen(!explorerOpen);

    if (explorerOpen) {
      setFileTableMdWidth(12);
      setFileTableLgWidth(12);
    } else {
      setFileTableMdWidth(8);
      setFileTableLgWidth(9);
    }
  };

  // Headle folder click of File Explorer.
  const handleTreeViewToggle = (event, nodeId) => {
    setExpanded(nodeId);
  };

  const handleTreeViewSelect = (event, nodeId) => {
    setSelected(nodeId);
    setFileList([]);

    if (nodeId !== -9999) {
      setPropertyTotal(0);
      fetchFileListShowLoading();
      fetchFileList(nodeId);
    }
  };

  // Handle Dialog Open and close
  const createFolderDialogHandleOpen = () => {
    if (folderSelectedResult === null) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No folder specify. Please select the folder.",
      });
      return;
    }

    if (!folderSelectedResult.rights.create) {
      buildAlert({
        // Alert Type: success, info, warning, error
        type: "warning",
        title: "warning Alert",
        content: "No folder rights to create folder.",
      });
      return;
    }

    setAddFolderDialogOpen(true);
  };

  const createFolderDialogHandleClose = () => {
    setAddFolderDialogOpen(false);

    setCreateFolderValidationErrors("");
    setTextFolderName("");
  };

  const propertyDetailDialogHandleOpen =
    (folderSeq, propertySeq, primaryPropertyFileSeq, primaryFilename) =>
    (event) => {
      setPrimaryFilename(primaryFilename);

      dispatch({
        type: "SELECTED_FOLDER",
        data: folderSeq,
      });

      dispatch({
        type: "FILE_SELECT",
        data: propertySeq,
      });

      dispatch({
        type: "PRIMARY_PROPERTY_FILE_SEQ",
        data: primaryPropertyFileSeq,
      });

      setWebViewerProps({
        from: "API",
        autoLoading: false,
        file_cat: "dms",
        file_ref: primaryPropertyFileSeq,
        file_name: primaryFilename,
      });
      if (!folderRight) {
        let rights = fileList.find(
          (obj) => obj.property_seq === propertySeq
        ).folder_right;

        setFolderRight(rights);
      }

      // if (folderRight) {

      // 	if (folderRight.write) {
      // 		setFilePropertyDetailDialogBtn([
      // 			{
      // 				buttonName: "Annotation Publish",
      // 				buttonFunction: annotationPublishActionHandleOpen,
      // 				chooseFile: false
      // 			},
      // 			{
      // 				buttonName: "Save Property",
      // 				buttonFunction: propertyDetailUpdateActionHandleOpen,
      // 				chooseFile: false
      // 			},
      // 		])
      // 	}
      // 	else {
      // 		setFilePropertyDetailDialogBtn([])
      // 	}
      // }

      setPropertyDetailDialogOpen(true);
    };

  const propertyDetailDialogHandleClose = () => {
    setPropertyDetailDialogOpen(false);
  };

  const advancedSearchDialogHandleOpen = () => {
    setFileSearchDialogOpen(true);
  };

  const advancedSearchDialogHandleClose = () => {
    setFileSearchDialogOpen(false);
  };

  // Confirm dialog
  const confirmDialogHandleOpen = (data) => {
    setConfirmDialogData({
      dialogTitle: data.dialogTitle,
      dialogContent: data.dialogContent,
      confirmAction: data.confirmAction,
      cancelAction: data.cancelAction,
    });

    setConfirmDialogOpen(true);
  };

  const confirmDialogHandleClose = () => {
    setConfirmDialogOpen(false);
  };

  // Fetch Data
  const fetchFolderList = () => {
    return async (dispatch) => {
      const res = await getFolderList();

      fetchFolderListHideLoading();

      dispatch({
        type: "FETCH_FOLDER_LIST",
        data: res.data,
      });
    };
  };

  const fetchFileList = (folderSeq, page = 1) => {
    getFileList(folderSeq, page).then((x) => {
      if (x.metadata.current_page < x.metadata.total_page) {
        setHasNext(true);
        setNextPage(x.metadata.current_page + 1);
      } else {
        setHasNext(false);
      }

      setFileList(x.data);

      fetchFileListHideLoading();
    });
  };

  const clearFileList = () => {
    return async (dispatch) => {
      const res = [];
      dispatch({
        type: "CLEAR_FILE_LIST",
        data: res,
      });
    };
  };

  // Input element status change
  const handleNewFolderNameChange = (event) => {
    const { value } = event.target;
    setTextFolderName(value);

    createFolderValidate(value);
  };

  // Toolbar Button function
  const createFolderAction = () => {
    if (!Array.isArray(selected)) {
      if (createFolderValidate()) {
        createFolderDialogHandleClose();

        createFolder(selected, textFolderName)
          .then((res) => {
            // console.log("Folder Create Successful")
            // console.log(res)

            setFolderCreated(res.data[0].folder_seq);
            setTextFolderName("");

            buildAlert({
              // Alert Type: success, info, warning, error
              type: "success",
              title: "Success Alert",
              content: "The folder have been created",
            });
          })
          .catch((error) => {
            buildAlert({
              // Alert Type: success, info, warning, error
              type: "error",
              title: "Error Alert",
              content: "The folder create failure",
            });
          });
      }
    }
  };

  const removeFolderAction = () => {
    if (!Array.isArray(selected)) {
      removeFolder(selected)
        .then((res) => {
          console.log("Folder Delete Successful");
          console.log(res);

          setFolderRemoved(selected);

          buildAlert({
            // Alert Type: success, info, warning, error
            type: "success",
            title: "Success Alert",
            content: "The folders have been removed",
          });
        })
        .catch((error) => {
          buildAlert({
            // Alert Type: success, info, warning, error
            type: "error",
            title: "Error Alert",
            content: "Folder remove failure",
          });
        });
    }
  };

  const removeFileAction = async () => {
    console.log("*** propertySeqRedux (remove) : ", propertySeqRedux);

    removeFile(propertySeqRedux)
      .then((res) => {
        console.log("File Delete Successful");
        console.log(res);

        setFileRemoved(propertySeqRedux);

        setPropertySelected([]);
        dispatch({
          type: "FILE_DISSELECT",
          data: [],
        });

        buildAlert({
          // Alert Type: success, info, warning, error
          type: "success",
          title: "Success Alert",
          content: "Files have been removed",
        });
      })
      .catch((error) => {
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "error",
          title: "Error Alert",
          content: "File remove failure",
        });
      });
  };

  const movePropertyAction = () => {
    moveProperty(propertySeqRedux, selected)
      .then((res) => {
        console.log("Move File Successful");
        console.log(res);

        buildAlert({
          // Alert Type: success, info, warning, error
          type: "success",
          title: "Success Alert",
          content: "The files have been moved",
        });

        setResetFileSelect(true);
        setResetMovePropertyControl(true);
        setExplorerRefresh(res.code);
      })
      .catch((error) => {
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "error",
          title: "Error Alert",
          content: "The file move failure",
        });
      });
  };

  const propertyDownloadAction = () => {
    // setProgressTitle("Downloading...")
    // setLockScreenLoadingOpen(true)

    // propertyDownload(propertySeqRedux).then(() => {
    // 	console.log("All property file download successful.")

    // 	setResetFileSelect(true)
    // 	setLockScreenLoadingOpen(false)
    // 	setProgressTitle("")
    // })
    buildAlert({
      // Alert Type: success, info, warning, error
      type: "warning",
      title: "Warning Alert",
      content: `Service Under Maintenance. Please open File Detail to download file`,
    });
  };

  const annotationPublishActionHandleOpen = () => {
    confirmDialogHandleOpen({
      dialogTitle: "Annotation Publish",
      dialogContent: "Are you sure to save and publish the annotation.",
      confirmAction: annotationPublishAction,
      cancelAction: annotationPublishActionHandleClose,
    });
  };

  const annotationPublishActionHandleClose = () => {
    confirmDialogHandleClose();
  };

  const annotationPublishAction = () => {
    const instance = WebViewer.getInstance(
      document.getElementById("webviewer")
    );
    const { annotManager } = instance;

    annotManager
      .exportAnnotations({ links: false, widgets: false })
      .then((xfdfString) => {
        saveAnnotation({
          file_seq: propertyFileSeq,
          file_cat: "dms",
          annotation: xfdfString,
        }).then((res) => {
          console.log(
            "*****************************************************************************"
          );
          console.log("Annotation Save and Publish Successful");
          console.log(res);
          console.log(
            "*****************************************************************************"
          );

          store.set("annotActive", false);

          annotationPublishActionHandleClose();
          buildAlert({
            // Alert Type: success, info, warning, error
            type: "success",
            title: "success Alert",
            content: "The annotations have been saved and published",
          });
        });
      });
  };

  const propertyDetailUpdateActionHandleOpen = () => {
    if (validate()) {
      if (store.get("annotActive")) {
        confirmDialogHandleOpen({
          dialogTitle: "Save Property",
          dialogContent:
            "Have annotation update, Are you sure to save the annotation and property",
          confirmAction: updateAnnotationPropertyAction,
          cancelAction: propertyDetailUpdateActionHandleClose,
        });
      } else {
        confirmDialogHandleOpen({
          dialogTitle: "Save Property",
          dialogContent: "Are you sure to save the property change.",
          confirmAction: propertyDetailUpdateAction,
          cancelAction: propertyDetailUpdateActionHandleClose,
        });
      }
    }
  };

  const propertyDetailUpdateActionHandleClose = () => {
    confirmDialogHandleClose();
  };

  const propertyDetailUpdateAction = () => {
    updatePropertyDetail(propertySeqRedux, propertyDetailResult)
      .then((res) => {
        console.log("Update Property Detail Successful");
        console.log(res);

        setFileRefresh(res.code);

        propertyDetailUpdateActionHandleClose();
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "success",
          title: "Success Alert",
          content: "The property detail have been saved",
        });
      })
      .catch((error) => {
        propertyDetailUpdateActionHandleClose();
        buildAlert({
          // Alert Type: success, info, warning, error
          type: "error",
          title: "Error Alert",
          content: "The property detail update failure",
        });
      });
  };

  const updateAnnotationPropertyAction = () => {
    propertyDetailUpdateAction();
    annotationPublishAction();
  };

  const advancedSearchAction = () => {
    const page = 1;

    advancedSearch(advancedSearchRedux, "", page).then((res) => {
      setAdvancedSearchNav();
      setPropertyTotal(res.data.length);
      setExpanded("-9999");
      setSelected("-9999");
      setFileList(res.data);

      advancedSearchDialogHandleClose();
    });
  };

  const setPropertyTotal = (numberOfRecord) => {
    folderList[0].property_count = numberOfRecord;
  };

  const setAdvancedSearchNav = () => {
    dispatch({
      type: "FOLDER_NAV",
      data: [folderList[0].name],
    });
  };

  // Dialog button setting
  const createFolderDialogBtn = [
    {
      buttonName: "Create",
      buttonFunction: createFolderAction,
      chooseFile: false,
    },
  ];

  const fileSearchDialogBtn = [
    {
      buttonName: "SEARCH",
      buttonFunction: advancedSearchAction,
      chooseFile: false,
    },
  ];

  // const filePropertyDetailDialogBtn_read = []

  // const filePropertyDetailDialogBtn_readWrite = [
  // 	{
  // 		buttonName: "Annotation Publish",
  // 		buttonFunction: annotationPublishActionHandleOpen,
  // 		chooseFile: false
  // 	},
  // 	{
  // 		buttonName: "Save Property",
  // 		buttonFunction: propertyDetailUpdateActionHandleOpen,
  // 		chooseFile: false
  // 	},
  // ]

  let filePropertyDetailDialogBtn = [];
  if (userModulesRedux !== null) {
    userModulesRedux.map((module) => {
      if (module.path === "/efiling") {
        if (module.rights.modifiable || module.rights.administrable) {
          filePropertyDetailDialogBtn = [
            {
              buttonName: "Annotation Publish",
              buttonFunction: annotationPublishActionHandleOpen,
              chooseFile: false,
            },
            {
              buttonName: "Save Property",
              buttonFunction: propertyDetailUpdateActionHandleOpen,
              chooseFile: false,
            },
          ];
        } else {
          filePropertyDetailDialogBtn = [
            {
              buttonName: "Save Property",
              buttonFunction: propertyDetailUpdateActionHandleOpen,
              chooseFile: false,
            },
          ];
        }
      }
    });
  }

  // Build Alert
  const buildAlert = (data) => {
    setAlertData({
      type: data.type,
      title: data.title,
      content: data.content,
    });
    setAlertOpen(true);
  };

  // Input Validation
  const createFolderValidate = (fieldValues = textFolderName) => {
    const temp = fieldValues !== "" ? "" : "This field is required";
    setCreateFolderValidationErrors(temp);

    if (fieldValues === textFolderName) {
      return Object.values(temp).every((x) => x == "");
    }
  };

  // Input Validation
  const validate = (fieldValues = propertyDetailResult) => {
    let temp = { ...validationErrors };

    // if ("ref_no" in fieldValues) {
    // 	temp.ref_no = fieldValues.ref_no ? "" : "This field is required"
    // }

    // if ("file_subject" in fieldValues) {
    // 	temp.file_subject = fieldValues.file_subject ? "" : "This field is required"
    // }

    // if ("inout" in fieldValues) {
    // 	temp.inout = fieldValues.inout !== "NA" ? "" : "This field is required"
    // }

    // if ("company_name" in fieldValues) {
    // 	temp.company_name = fieldValues.company_name !== "" ? "" : "This field is required"
    // }

    if ("company_seq" in fieldValues) {
      temp.company_seq =
        fieldValues.company_seq !== "" && fieldValues.company_seq !== 0
          ? ""
          : "This field is required";
    }

    setValidationErrors({ ...temp });

    if (fieldValues == propertyDetailResult) {
      return Object.values(temp).every((x) => x == "");
    }
  };

  // useEffect(() => {

  // 	console.log("*** propertySeqRedux (useEffect) : ", propertySeqRedux)

  // 	if (folderRight) {

  // 		if (!folderRight.write) {
  // 			setFilePropertyDetailDialogBtn([])
  // 		}

  // 		// if (folderRight.write) {
  // 		// 	setFilePropertyDetailDialogBtn([
  // 		// 		{
  // 		// 			buttonName: "Annotation Publish",
  // 		// 			buttonFunction: annotationPublishActionHandleOpen,
  // 		// 			chooseFile: false
  // 		// 		},
  // 		// 		{
  // 		// 			buttonName: "Save Property",
  // 		// 			buttonFunction: propertyDetailUpdateActionHandleOpen,
  // 		// 			chooseFile: false
  // 		// 		},
  // 		// 	])
  // 		// }
  // 		// else {
  // 		// 	setFilePropertyDetailDialogBtn([])
  // 		// }
  // 	}
  // }, [folderRight])

  useEffect(() => {
    fetchFolderListShowLoading();

    dispatch(fetchFolderList());
    dispatch(clearFileList());

    if (!Array.isArray(selected)) {
      fetchFileList(selected, nextPage - 1);
    }
  }, [
    fileRefresh,
    folderCreated,
    folderRemoved,
    fileRemoved,
    fileUploaded,
    explorerRefresh,
  ]);

  return (
    // console.log("File List | Variable: fileList (FileManager.js)"),
    // console.log(fileList),

    // console.log("Folder Right | Variable: folderRight (FileManager.js)"),
    // console.log(folderRight),

    // console.log("Property Files Detail Information (Redux) | Variable: propertyDetailResult (FileManager.js)"),
    // console.log(propertyDetailResult),

    // console.log("property Seq (Redux) | Variable: propertySeqRedux (FileManager.js)"),
    // console.log(propertySeqRedux),

    // console.log("filePropertyDetailDialogBtn | Variable: filePropertyDetailDialogBtn (FileManager.js)"),
    // console.log(filePropertyDetailDialogBtn),

    // console.log("---------------------------------------------------------------------------------"),

    <Fragment>
      <div>
        <LockScreenLoading
          open={lockScreenLoadingOpen}
          circleSize={200}
          progressTitle={progressTitle}
        />

        <ConfirmDialog
          open={confirmDialogOpen}
          dialogTitle={confirmDialogData.dialogTitle}
          dialogContent={confirmDialogData.dialogContent}
          confirmAction={confirmDialogData.confirmAction}
          cancelAction={confirmDialogData.cancelAction}
        />

        <Alert
          open={alertOpen}
          alertType={alertData.type}
          alertTitle={alertData.title}
          alertContent={alertData.content}
          handleClose={() => setAlertOpen(false)}
        />

        <FullScreenDialog
          open={propertyDetailDialogOpen}
          dialogTitle={"File Property"}
          formContent={
            <PropertyDetailForm
              folderRight={folderRight}
              propertyFileSeq={propertyFileSeq}
              setPropertyFileSeq={setPropertyFileSeq}
              primaryFilename={primaryFilename}
              webViewerProps={webViewerProps}
              setWebViewerProps={setWebViewerProps}
              validate={validate}
              validationErrors={validationErrors}
            />
          }
          button={filePropertyDetailDialogBtn}
          handleClose={propertyDetailDialogHandleClose}
        />

        <NormalDialog
          open={addFolderDialogOpen}
          dialogTitle={"Create Folder"}
          formContent={
            <CreateFolderForm
              error={createFolderValidationErrors !== "" ? true : false}
              helperText={createFolderValidationErrors}
              handleInputChange={handleNewFolderNameChange}
            />
          }
          // xs = extraSmall, sm = small, md = medium, lg = large, xl = extraLarge}
          size={"xs"}
          button={createFolderDialogBtn}
          handleClose={createFolderDialogHandleClose}
        />

        <NormalDialog
          open={fileSearchDialogOpen}
          dialogTitle={"Advanced Search"}
          formContent={<AdvancedSearchForm />}
          // xs = extraSmall, sm = small, md = medium, lg = large, xl = extraLarge}
          size={"sm"}
          // height={700}
          button={fileSearchDialogBtn}
          handleClose={advancedSearchDialogHandleClose}
        />
      </div>

      <Box className={classes.navBar}>
        <ToggleButton
          value="explorer-switch"
          selected={explorerSwitchSelected}
          aria-label="explorer-switch"
          onChange={() => {
            setExplorerSwitchSelected(!explorerSwitchSelected);
          }}
          onClick={handleExplorerSwitch}
          size="small"
          className={classes.fileNavIcon}
        >
          <MenuOpenIcon fontSize="small" />
        </ToggleButton>

        <Box className={classes.fileManagerPath}>
          <FileManagerNav folderPath={folderNavResult} />
        </Box>
      </Box>

      <div className={classes.fileManager}>
        <Grid container spacing={1}>
          <Fade
            in={explorerOpen}
            // disableStrictModeCompat={true}
            mountOnEnter
            unmountOnExit
          >
            <Grid item sm={12} md={4} lg={3}>
              <Paper
                className={classes.fileManagerLeftPaper}
                style={{
                  "--screen-height": screenSize,
                }}
              >
                <FileManagerExplorer
                  folderData={folderList}
                  setFolderRight={setFolderRight}
                  handleTreeViewToggle={handleTreeViewToggle}
                  handleTreeViewSelect={handleTreeViewSelect}
                  expanded={expanded}
                  selected={selected}
                  loading={fetchFolderListLoading}
                />
              </Paper>
            </Grid>
          </Fade>

          <Grid item sm={12} md={fileTableMdWidth} lg={fileTableLgWidth}>
            <Paper
              className={classes.fileManagerRightPaper}
              style={{
                "--screen-height": screenSize,
              }}
            >
              <EnhancedTable
                loading={fetchFileListLoading}
                folderRight={folderRight}
                fileList={fileList}
                hasMore={hasNext}
                fileManagerType={fileManagerType}
                // Selected data storage
                folderSelected={selected}
                propertySelected={propertySelected}
                setPropertySelected={setPropertySelected}
                quickSearchKeyWord={quickSearchKeyWord}
                setQuickSearchKeyWord={setQuickSearchKeyWord}
                // Reset selected storage
                resetFileSelect={resetFileSelect}
                setResetFileSelect={setResetFileSelect}
                resetMovePropertyControl={resetMovePropertyControl}
                setResetMovePropertyControl={setResetMovePropertyControl}
                setFileUploaded={setFileUploaded}
                // Toolbar button action
                createFolderAction={createFolderAction}
                removeFolderAction={removeFolderAction}
                removeFileAction={removeFileAction}
                movePropertyAction={movePropertyAction}
                propertyDownloadAction={propertyDownloadAction}
                // Button Contral
                createFolderDialogHandleOpen={createFolderDialogHandleOpen}
                confirmDialogHandleOpen={confirmDialogHandleOpen}
                confirmDialogHandleClose={confirmDialogHandleClose}
                propertyDetailDialogHandleOpen={propertyDetailDialogHandleOpen}
                advancedSearchDialogHandleOpen={advancedSearchDialogHandleOpen}
                setFileSearchDialogOpen={setFileSearchDialogOpen}
              />
            </Paper>
          </Grid>
        </Grid>
      </div>
    </Fragment>
  );
};

export default FileManager;
